import { codeFrameColumns } from '@babel/code-frame';
import chalk from 'chalk';
import * as fs from 'fs';
import * as os from 'os';
import * as path from 'path';

import { Matchers } from './Matchers';
import { Failure, Parser } from './Parser';
import { switchRegex } from './switchRegex';
import {
  BREADCRUMB,
  COMPLETION,
  ERROR,
  FAIL,
  INDENT,
  MEASURE,
  PASS,
  PENDING,
  WARNING,
} from './utils/symbols';

enum Status {
  Pass = 'pass',
  Fail = 'fail',
  Pending = 'pending',
  Error = 'error',
  Completion = 'completion',
  Measure = 'measure',
}

function highlightLastPathComponent(filePath: string): string {
  return chalk.dim(path.dirname(filePath) + '/') + path.basename(filePath);
}

function format(command: string, argumentText = '', success = true) {
  const symbol = statusSymbol(success ? Status.Completion : Status.Fail);
  return [symbol, chalk.bold(command), argumentText].join(' ').trim();
}

function formatTest(testCase: string, status: Status) {
  return [statusSymbol(status), testCase].join(' ').trim();
}

function heading(prefix: string, text: string, description: string): string {
  return [prefix, chalk.white(text), description].join(' ').trim();
}

function statusSymbol(status: Status) {
  switch (status) {
    case Status.Pass:
      return chalk.green(PASS);
    case Status.Fail:
      return chalk.red(FAIL);
    case Status.Pending:
      return chalk.cyan(PENDING);
    case Status.Error:
      return chalk.red(ERROR);
    case Status.Completion:
      return chalk.white(COMPLETION);
    case Status.Measure:
      return chalk.magenta(MEASURE);
    default:
      return '';
  }
}

function coloredTime(time: string) {
  const flt = parseFloat(time);
  if (flt >= 0 && flt <= 0.025) {
    return time;
  } else if (flt >= 0.026 && flt <= 0.1) {
    return chalk.yellow(time);
  }
  return chalk.red(time);
}

function capitalize(string: string): string {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

function relativePath(projectRoot: string, filePath: string): string {
  return slash(path.relative(projectRoot, filePath));
}

function formatBreadCrumb(fileName: string, target?: string, project?: string): string {
  // TODO: Simplify
  return [project === target ? project : [project, target].filter(Boolean).join(`/`), fileName]
    .filter(Boolean)
    .join(` ${BREADCRUMB} `);
}

function getAppRoot(filePath: string): string {
  let appRoot = filePath;

  const isBuildFolder = (path: string) => /((Debug|Release)-[^/\s\d]*$)|(.app$)/.test(path);

  while (!isBuildFolder(appRoot) && appRoot.length > 1) {
    appRoot = path.dirname(appRoot);
  }
  return isBuildFolder(appRoot) ? appRoot : '';
}

export type FileOperation = {
  type:
    | 'Analyze'
    | 'GenerateDSYMFile'
    | 'Ld'
    | 'Libtool'
    | 'ProcessPCH'
    | 'ProcessInfoPlistFile'
    | 'CodeSign'
    | 'Touch'
    | 'CompileC'
    | 'CompileSwift'
    | 'CompileXIB'
    | 'CompileStoryboard';
  filePath: string;
  fileName: string;
  arch?: string;
  // normal
  linkType?: string;
  target?: string;
  project?: string;
};

export type ConfigurationOperation = {
  type: 'Analyze' | 'Aggregate' | 'Build' | 'Clean';
  configuration: string;
  target?: string;
  project?: string;
};

export type CopyFileProps = {
  type: 'CpResource' | 'CopyStringsFile' | 'CopyPlistFile' | 'CpHeader';
  from: string;
  to: string;
  target?: string;
  project?: string;
};

export class Formatter {
  static format = format;
  static formatBreadCrumb = formatBreadCrumb;
  static getAppRoot = getAppRoot;
  static highlightLastPathComponent = highlightLastPathComponent;
  static relativePath = relativePath;

  _parser: Parser | undefined;

  get parser(): Parser {
    if (this._parser) {
      return this._parser;
    }
    this._parser = new Parser(this);
    return this._parser;
  }

  errors: string[] = [];

  warnings: string[] = [];

  constructor(
    public props: {
      // isASCII: boolean;
      // isColorDisabled: boolean;
      projectRoot: string;
      /** Sets the max character length for a warning before cropping the preview. Useful for minified files that can be upwards of a thousand characters long. */
      maxWarningLineLength?: number;
    }
  ) {}

  pipe(data: string): string[] {
    const lines: string[] = [];
    data.split(os.EOL).forEach(line => {
      const results = this.parser.parse(line);
      if (typeof results === 'string' && results.trim()) {
        lines.push(results);
      }
    });
    this.checkForBundlingErrors(lines);
    return lines;
  }

  private dimConfiguration(configuration: string): string {
    return chalk.dim(`(${configuration})`);
  }

  getTitleForConfigurationType(type: ConfigurationOperation['type']): string {
    switch (type) {
      case 'Clean':
        return 'Cleaning';
      case 'Aggregate':
        return 'Aggregate';
      case 'Analyze':
        return 'Analyzing';
      case 'Build':
        return 'Building';
      default:
        return 'Unknown';
    }
  }

  formatTarget(props: ConfigurationOperation): string {
    return format(
      this.getTitleForConfigurationType(props.type),
      this.dimConfiguration(formatBreadCrumb(props.configuration, props.target, props.project))
    );
  }

  formatCopy({ from, to }: CopyFileProps): string {
    const relativeFile = relativePath(this.props.projectRoot, from);
    const appFileRoot = getAppRoot(to);
    const relativeAppFile = relativePath(appFileRoot, to);
    return format('Copying  ', [relativeFile, relativeAppFile].join(' ➜ '));
  }

  getFileOperationTitle(type: FileOperation['type']): string {
    switch (type) {
      case 'Analyze':
        return 'Analyzing';
      case 'GenerateDSYMFile':
        return `Generating debug`;
      case 'Ld':
        return 'Linking  ';
      case 'Libtool':
        return 'Packaging';
      case 'ProcessPCH':
        return 'Precompiling';
      case 'ProcessInfoPlistFile':
        return 'Preparing';
      case 'CodeSign':
        return 'Signing  ';
      case 'Touch':
        return 'Creating ';
      case 'CompileC':
      case 'CompileSwift':
      case 'CompileXIB':
      case 'CompileStoryboard':
        return 'Compiling';
      default:
        // Unknown file operation
        return '';
    }
  }

  formatFileOperation(props: FileOperation): string {
    const title = this.getFileOperationTitle(props.type);

    switch (props.type) {
      case 'Analyze':
        return format(title, formatBreadCrumb(props.fileName, props.target, props.project));
      case 'GenerateDSYMFile':
        return format(title, formatBreadCrumb(`'${props.fileName}'`, props.target, props.project));
      case 'Ld':
        return format(title, formatBreadCrumb(props.fileName, props.target, props.project));
      case 'Libtool':
        return format(title, formatBreadCrumb(props.fileName, props.target, props.project));
      case 'ProcessPCH':
        return format(title, formatBreadCrumb(props.fileName, props.target, props.project));
      case 'ProcessInfoPlistFile':
        return format(title, formatBreadCrumb(props.fileName, props.target, props.project));
      case 'CodeSign':
        return format(title, formatBreadCrumb(props.fileName, props.target, props.project));
      case 'Touch':
        return format(title, props.fileName);
      case 'CompileC':
      case 'CompileSwift':
      case 'CompileXIB':
      case 'CompileStoryboard':
        return format(title, formatBreadCrumb(props.fileName, props.target, props.project));
      default:
        // Unknown file operation
        return '';
    }
  }

  formatPhaseSuccess(phaseName: string, duration?: string): string {
    return format(
      capitalize(phaseName.toLowerCase()),
      `Succeeded${duration ? ` (${duration})` : ''}`,
      true
    );
  }

  formatPhaseScriptExecution(scriptName: string, target?: string, project?: string): string {
    // TODO: Track (scriptName === '[CP] Copy XCFrameworks')
    return format('Executing', formatBreadCrumb(`'${scriptName}'`, target, project));
  }

  formatPreprocess(file: string): string {
    return format('Preprocessing', file);
  }

  formatShellCommand(command: string, args: string): string {
    return '';
  }

  formatCompileCommand(compilerCommand: string, filePath: string): string {
    return '';
  }

  formatProcessPchCommand(filePath: string): string {
    return '';
  }

  formatWriteFile(file: string): string {
    return '';
  }

  formatOther(text: string): string {
    return '';
  }

  formatSingleLineCompileIssue(
    type: 'warning' | 'error',
    filePathAndLocation: string,
    fileName: string,
    reason: string,
    target: string,
    project: string
  ): string {
    // Allow disabling this warning.
    const { filePath, lineNumber, columnNumber } = splitPathInfo(filePathAndLocation);
    if (type === 'warning') {
      if (!this.shouldShowCompileWarning(filePath, lineNumber, columnNumber)) {
        return '';
      }
    }
    // Prevent `/foo/bar:1:1` instead using `/foo/bar` since it's a bit more focused.
    const sanitizedFilePath =
      lineNumber === '1' && columnNumber === '1' ? filePath : filePathAndLocation;

    // Get the `Project/Target` prefix.
    const packageName = [project, target].join('/');
    // Choose a color.
    const color = type === 'warning' ? chalk.yellow : chalk.red;
    const platform = color.bold(`${packageName}:`);
    // Choose a symbol.
    const symbol = type === 'warning' ? WARNING : ERROR;

    // Get a more concise file path when the issue is inside the build folder.
    const appFileRoot = getAppRoot(sanitizedFilePath);
    const relativeAppFile = appFileRoot
      ? chalk.gray('[app]/') + relativePath(appFileRoot, sanitizedFilePath)
      : sanitizedFilePath;

    // Create the message.
    const results = chalk`${symbol} ${platform} ${reason.trim()}\n   {gray └─${relativeAppFile}}`;

    // Ensure we track the message
    if (type === 'warning') {
      this.warnings.push(results);
    } else {
      this.errors.push(results);
    }
    return results;
  }

  // These are like comments but for Xcode logs.
  formatRemark(msg: string): string {
    return '';
  }

  formatEmitSwiftModule(
    type: 'normal' | string,
    arch: 'x86_64' | string,
    target?: string,
    project?: string
  ): string {
    return '';
  }
  formatCompileSwiftSources(
    type: 'normal' | string,
    arch: 'x86_64' | string,
    pkg: string | 'com.apple.xcode.tools.swift.compiler',
    target?: string,
    project?: string
  ): string {
    return '';
  }

  formatCleanRemove(msg: string): string {
    return '';
  }

  formatWriteAuxiliaryFiles(text: string): string {
    return '';
  }

  formatTiffutil(file: string): string {
    return format('Validating', file);
  }

  formatCheckDependencies(text: string): string {
    return format('Check Dependencies');
  }

  formatWillNotBeCodeSigned(message: string): string {
    const results = `${chalk.yellow(WARNING + ' ' + message)}`;
    this.warnings.push(results);
    return results;
  }

  // COMPILER / LINKER ERRORS AND WARNINGS
  /**
   *
   * @param fileName 'SampleTest.m',
   * @param filePathAndLocation '/Users/foo/bar.m:12:59',
   * @param reason 'expected identifier',
   * @param line '                [[thread should] equal:thread.];',
   * @param cursor '                                           ^'
   */
  formatCompileError(
    fileName: string,
    filePathAndLocation: string,
    reason: string,
    line: string,
    cursor: string
  ): string {
    const { filePath, lineNumber, columnNumber } = splitPathInfo(filePathAndLocation);
    const results = formatWarningOrError({
      isError: true,
      filePath,
      reason,
      cursor,
      lineText: line,
      lineNumber,
      columnNumber,
      projectRoot: this.props.projectRoot,
      maxWarningLineLength: this.props.maxWarningLineLength,
    });
    this.errors.push(results);
    return results;
  }

  formatError(message: string): string {
    const results = switchRegex(message, [
      [
        Matchers.Errors.UNSUPPORTED_ENTITLEMENT_MATCHER,
        ([, $1, $2, $3, $4, $5]) => {
          return this.formatUnsupportedEntitlementError(
            $1,
            $2,
            $3 as 'capability' | 'entitlement',
            $4,
            $5
          );
        },
      ],
      [null, () => this.formatGenericError(message)],
    ]);

    this.errors.push(results);

    return results;
  }

  /**
   * In: `error: Provisioning profile "iOS Team Provisioning Profile: *" doesn't support the Push Notifications capability. (in target 'yolo90' from project 'yolo90')`
   * Out: `❌  yolo90/yolo90: Provisioning Profile "iOS Team Provisioning Profile: *" does not support the Push Notifications capability.`
   *
   * In: `error: Provisioning profile "iOS Team Provisioning Profile: *" doesn't include the aps-environment entitlement. (in target 'yolo90' from project 'yolo90')`
   * Out: `❌  yolo90/yolo90: Entitlements file defines the value "aps-environment" which is not registered for profile "iOS Team Provisioning Profile: *".`
   *
   * @param profileName `"iOS Team Provisioning Profile: *"`
   * @param entitlementName `Push Notifications` | `aps-environment`
   * @param entitlementType `capability` | `entitlement`
   * @param target boost-for-react-native
   * @param project Pods
   */
  formatUnsupportedEntitlementError(
    profileName: string,
    entitlementName: string,
    entitlementType: 'capability' | 'entitlement',
    target?: string,
    project?: string
  ): string {
    const packageName = [project, target].join('/');
    const platform = chalk.red.bold(`${packageName}:`);

    if (entitlementType === 'capability') {
      return chalk`${ERROR} ${platform} Provisioning Profile ${profileName} does not support the {red ${entitlementName}} capability.`;
    }
    return chalk`${ERROR} ${platform} Entitlements file defines the value {red "${entitlementName}"} which is not registered for profile ${profileName}.`;
  }

  formatFileMissingError(reason: string, filePath: string): string {
    const results = `\n${chalk.red(ERROR + ' ' + reason)} ${filePath}\n\n`;
    this.errors.push(results);
    return results;
  }

  formatLdWarning(reason: string): string {
    const results = switchRegex(reason, [
      [
        Matchers.Warnings.LINKER_METHOD_OVERRIDE,
        ([, $1, $2, $3, $4, $5]) => {
          return this.formatLdMethodOverride($1, [
            { filePath: $2, name: $3 },
            { filePath: $4, name: $5 },
          ]);
        },
      ],
      [
        Matchers.Warnings.LINKER_METHOD_SINGLE_OVERRIDE,
        ([, $1, $2, $3]) => {
          return this.formatLdMethodOverride($1, [{ filePath: $2, name: $3 }]);
        },
      ],
      [null, () => `${chalk.yellow(WARNING + ' ' + reason)}`],
    ]);

    if (results) {
      this.warnings.push(results);
    }

    return results;
  }

  formatUndefinedSymbols(message: string, symbol: string, reference: string): string {
    const symbols = chalk.gray(`┌─ Symbol: ${symbol}\n└─ Referenced from: ${reference}`);
    const results = `${chalk.red(ERROR + ' ' + message)}\n${symbols}\n`;
    this.errors.push(results);
    return results;
  }

  formatLdMethodOverride(
    methodName: string,
    collisions: { filePath: string; name: string }[]
  ): string {
    if (!this.shouldShowLinkerWarning(methodName, collisions)) {
      return '';
    }

    const formattedMessage = chalk.yellow(
      WARNING + ` ld: duplicate method '${chalk.bold(methodName)}' in`
    );
    const types = ['category', 'class'];
    const symbols = chalk.gray(
      collisions
        .map(({ filePath, name }, i) => {
          const appFileRoot = getAppRoot(filePath);
          const relativeAppFile = relativePath(appFileRoot, filePath);
          const branch = i === collisions.length - 1 ? '└─' : i === 0 ? '┌─' : '├─';
          return `${branch}${`[${types[i]}]`}: ${name} ${chalk.dim(relativeAppFile)}`;
        })
        .join('\n')
    );
    return `${formattedMessage}\n${symbols}\n`;
  }

  formatDuplicateSymbols(message: string, filePaths: string[], isWarning: boolean): string {
    const formattedMessage = isWarning
      ? chalk.yellow(WARNING + ' ' + message)
      : chalk.red(ERROR + ' ' + message);
    const symbols = chalk.gray(
      filePaths
        .map((p, i) => {
          const branch = i === filePaths.length - 1 ? '└─' : i === 0 ? '┌─' : '├─';
          return `${branch} ${path.basename(p)}`;
        })
        .join('\n')
    );
    const results = `${formattedMessage}\n${symbols}\n`;

    if (isWarning) {
      this.warnings.push(results);
    } else {
      this.errors.push(results);
    }
    return results;
  }

  /**
   * In: `The iOS Simulator deployment target 'IPHONEOS_DEPLOYMENT_TARGET' is set to 8.0, but the range of supported deployment target versions is 9.0 to 14.3.99. (in target 'boost-for-react-native' from project 'Pods')`
   * Out: `⚠️ Pods/boost-for-react-native: iOS@8.0 version mismatch. Expected >= 9.0 < 14.3.99`
   *
   * @param os iOS
   * @param deploymentTarget IPHONEOS_DEPLOYMENT_TARGET
   * @param version 8.0
   * @param minVersion 9.0
   * @param maxVersion 14.3.99
   * @param target boost-for-react-native
   * @param project Pods
   */
  formatVersionMismatchWarning(
    os: string,
    deploymentTarget: string,
    version: string,
    minVersion: string,
    maxVersion: string,
    target: string,
    project: string
  ): string {
    const packageName = [project, target].join('/');
    const platform = chalk.bold(`${packageName}:`);
    const packageNameWithVersion = chalk.greenBright(os) + chalk.cyan`@` + chalk.magenta(version);
    const expectedRange = `>= ${minVersion} <= ${maxVersion}`;
    return `${WARNING} ${platform} ${packageNameWithVersion} deployment version mismatch, expected ${expectedRange}`;
  }

  /**
   * In: `warning: [CP] Vendored binary '/Users/evanbacon/Library/Developer/Xcode/DerivedData/yolo67-hcjsxsdqyxnsgdednlbpylgeffja/Build/Intermediates.noindex/Pods.build/Debug-iphonesimulator/hermes-engine.build/DerivedSources/hermes.framework.dSYM/Contents/Resources/DWARF/hermes' contains architectures (armv7 armv7s arm64) none of which match the current build architectures (x86_64).`
   * Out: `⚠️  Vendored binary '[app]/hermes-engine.build/DerivedSources/hermes.framework.dSYM/Contents/Resources/DWARF/hermes' does not support current build architecture (x86_64). Supported architectures: armv7, armv7s, arm64.`
   *
   * @param os iOS
   * @param deploymentTarget IPHONEOS_DEPLOYMENT_TARGET
   * @param version 8.0
   * @param minVersion 9.0
   * @param maxVersion 14.3.99
   * @param target boost-for-react-native
   * @param project Pods
   */
  formatMissingArchitectureWarning(
    binaryPath: string,
    architectures: string[],
    currentArchitectures: string[]
  ): string {
    const appFileRoot = getAppRoot(binaryPath);
    const relativeAppFile = appFileRoot
      ? chalk.gray('[app]/') + relativePath(appFileRoot, binaryPath)
      : binaryPath;

    const architectureString = currentArchitectures.length === 1 ? 'architecture' : 'architectures';
    const supportedString = chalk.dim(`Supported architectures: ${architectures.join(', ')}.`);
    return (
      chalk.yellow(
        `${WARNING} Vendored binary '${relativeAppFile}' does not support current build ${architectureString} (${chalk.bold(
          currentArchitectures.join(', ')
        )}). `
      ) + supportedString
    );
  }

  /**
   * In: `Skipping duplicate build file in Compile Sources build phase: /Users/evanbacon/Documents/GitHub/expo/ios/Exponent/Kernel/ReactAppManager/EXReactAppManager.mm (in target 'Exponent' from project 'Exponent')`
   * Out:
   * `⚠️ Skipping duplicate file: Exponent/Kernel/ReactAppManager/EXReactAppManager.mm:
   *    Remove: Exponent » Exponent » Build Phases » Compile Sources » EXReactAppManager.mm`
   *
   * @param filePath
   * @param buildPhase 'Compile Sources'
   * @param target Exponent-watch-app
   * @param project Exponent
   */
  formatDuplicateFileCompilerWarning(
    filePath: string,
    buildPhase: string,
    target: string,
    project: string
  ): string {
    const message = `${chalk.yellow`Skipping duplicate file:`} ${relativePath(
      this.props.projectRoot,
      filePath
    )}`;
    const fileName = path.basename(filePath);
    const crumbs = chalk.gray(
      'Remove: ' +
        ['Xcode', `${project}/${target}`, 'Build Phases', buildPhase, fileName].join(
          ` ${BREADCRUMB} `
        )
    );

    return `${WARNING} ${message}\n   ${crumbs}\n`;
  }

  /**
   * In: `The Copy Bundle Resources build phase contains this target's Info.plist file '/Users/evanbacon/Documents/GitHub/expo/ios/Exponent/Supporting/Info.plist'. (in target 'Exponent' from project 'Exponent')`
   * Out:
   * `⚠️ Target's Info.plist file is incorrectly linked: Exponent/Supporting/Info.plist:
   *    Remove: Exponent » Exponent » Build Phases » Copy Bundle Resources » Info.plist`
   *
   * @param filePath
   * @param reservedFileDescription 'entitlements'
   * @param target Exponent-watch-app
   * @param project Exponent
   */
  formatReservedFileInCopyBundleResourcesCompilerWarning(
    filePath: string,
    reservedFileDescription: string,
    target: string,
    project: string
  ): string {
    const message = `${chalk.yellow`Target's ${chalk.bold(
      reservedFileDescription
    )} file is incorrectly linked:`} ${relativePath(this.props.projectRoot, filePath)}`;
    const fileName = path.basename(filePath);
    const crumbs = chalk.gray(
      'Remove: ' +
        ['Xcode', `${project}/${target}`, 'Build Phases', 'Copy Bundle Resources', fileName].join(
          ` ${BREADCRUMB} `
        )
    );

    return `${WARNING} ${message}\n   ${crumbs}\n`;
  }

  /**
   * In: `Run script build phase '[CP-User] [Hermes] Replace Hermes for the right configuration, if needed' will be run during every build because it does not specify any outputs. To address this warning, either add output dependencies to the script phase, or configure it to run in every build by unchecking "Based on dependency analysis" in the script phase. (in target 'hermes-engine' from project 'Pods')`
   * Out:
   * `⚠️ Script has ambiguous dependencies causing it to run on every build.
   *    To fix, go to: Xcode » Pods/hermes-engine » Build Phases » [CP-User] [Hermes] Replace Hermes for the right configuration, if needed
   *    Either: Uncheck "Based on dependency analysis", or select output files to trigger the script`
   *
   * @param script [CP-User] [Hermes] Replace Hermes for the right configuration, if needed
   * @param target Exponent-watch-app
   * @param project Exponent
   */
  formatAmbiguousRunScriptOutputsWarning(script: string, target: string, project: string): string {
    if (!this.shouldShowWarningInTarget({ target })) {
      return '';
    }

    const message = chalk.yellow`Script has ambiguous dependencies causing it to run on every build.`;
    const crumbs = chalk.gray(
      'To fix, go to: ' +
        ['Xcode', `${project}/${target}`, 'Build Phases', `'${script}'`].join(` ${BREADCRUMB} `)
    );

    const solution = chalk.gray(
      `Either: Uncheck "Based on dependency analysis", or select output files to trigger the script`
    );

    return `${WARNING} ${message}\n   ${crumbs}\n   ${solution}\n`;
  }

  formatMissingFileCompilerWarning(filePath: string): string {
    return `${WARNING} ${chalk.yellow`No such file or directory:`} ${filePath}`;
  }

  formatGenericError(message: string): string {
    return `\n${chalk.red(ERROR + ' ' + message)}\n\n`;
  }

  formatGenericWarning(message: string): string {
    return INDENT + chalk.yellow(message);
  }

  formatWarning(message: string): string {
    const results = switchRegex(message, [
      [
        Matchers.Warnings.MISSING_ARCHITECTURE,
        ([, $1, $2, $3]) => {
          return this.formatMissingArchitectureWarning(
            $1,
            $2?.split(' ').map(value => value.trim()),
            $3?.split(' ').map(value => value.trim())
          );
        },
      ],
      [
        Matchers.Warnings.VERSION_MISMATCH,
        ([, $1, $2, $3, $4, $5, $6, $7]) => {
          return this.formatVersionMismatchWarning($1, $2, $3, $4, $5, $6, $7);
        },
      ],
      [
        Matchers.Warnings.MISSING_FILE_COMPILER_WARNING_MATCHER,
        ([, $1]) => {
          return this.formatMissingFileCompilerWarning($1);
        },
      ],
      [
        Matchers.Warnings.SKIPPING_DUPLICATE_FILE,
        ([, $1, $2, $3, $4]) => {
          return this.formatDuplicateFileCompilerWarning($2, $1, $3, $4);
        },
      ],
      [
        Matchers.Warnings.TARGETS_FILE_INCLUDED,
        ([, $1, $2, $3, $4]) => {
          return this.formatReservedFileInCopyBundleResourcesCompilerWarning($2, $1, $3, $4);
        },
      ],
      [
        Matchers.Warnings.AMBIGUOUS_RUN_SCRIPT,
        ([, $1, $2, $3, $4]) => {
          return this.formatAmbiguousRunScriptOutputsWarning($1, $2, $3);
        },
      ],
      [null, () => this.formatGenericWarning(message)],
    ]);

    // If the warning wasn't skipped then add it.
    if (results) {
      this.warnings.push(results);
    }

    return results;
  }

  // TODO: see how we can unify formatError and formatCompileError,
  //       the same for warnings
  formatCompileWarning(
    fileName: string,
    filePathAndLocation: string,
    reason: string,
    line?: string,
    cursor?: string
  ): string {
    const { filePath, lineNumber, columnNumber } = splitPathInfo(filePathAndLocation);
    if (this.shouldShowCompileWarning(filePath, lineNumber, columnNumber)) {
      const results = formatWarningOrError({
        isError: false,
        filePath,
        reason,
        cursor,
        lineText: line,
        lineNumber,
        columnNumber,
        projectRoot: this.props.projectRoot,
        maxWarningLineLength: this.props.maxWarningLineLength,
      });
      if (results) {
        this.warnings.push(results);
      }
      return results;
    }
    return '';
  }

  shouldShowWarningInTarget(props: { target?: string }): boolean {
    return true;
  }

  shouldShowCompileWarning(filePath: string, lineNumber?: string, columnNumber?: string): boolean {
    return true;
  }

  shouldShowLinkerWarning(
    methodName: string,
    collisions: { filePath: string; name: string }[]
  ): boolean {
    return true;
  }

  formatPendingTest(suite: string, test: string): string {
    return INDENT + formatTest(`${test} [PENDING]`, Status.Pending);
  }

  formatPassingTest(suite: string, test: string, time: string): string {
    return INDENT + formatTest(`${test} (${coloredTime(time)} seconds)`, Status.Pass);
  }

  formatMeasuringTest(suite: string, test: string, time: string): string {
    return INDENT + formatTest(`${test} measured (${coloredTime(time)} seconds)`, Status.Measure);
  }

  formatFailingTest(suite: string, test: string, reason: string, filePath: string): string {
    return INDENT + formatTest(`${test}, ${reason}`, Status.Fail);
  }

  formatTestRunStarted(name: string): string {
    return heading('Test Suite', name, 'started');
  }

  formatTestSuiteStarted(name: string): string {
    return heading('', name, '');
  }

  formatTestRunFinished(name: string, time: string): string {
    return '';
  }

  // Will be printed by default. Override with '' if you don't want summary
  formatTestSummary(executedMessage: string, failuresPerSuite: Record<string, Failure[]>): string {
    const failures = this.formatFailures(failuresPerSuite);
    let finalMessage = '';
    if (!failures) {
      finalMessage = chalk.green(executedMessage);
    } else {
      finalMessage = chalk.red(executedMessage);
    }

    const text = [failures, finalMessage].join('\n\n\n').trim();
    return `\n\n${text}`;
  }

  formatFailures(failuresPerSuite: Record<string, Failure[]>): string {
    return Object.entries(failuresPerSuite)
      .map(([suite, failures]) => {
        const formattedFailures = failures.map(failure => this.formatFailure(failure)).join('\n\n');

        return `\n${suite}\n${formattedFailures}`;
      })
      .join('\n');
  }

  formatFailure(f: Failure): string {
    const { filePath, lineNumber, columnNumber } = splitPathInfo(f.filePath);

    return formatWarningOrError({
      isError: true,
      testName: f.testCase,
      filePath,
      reason: f.reason,
      // cursor,
      lineNumber,
      columnNumber,
      projectRoot: this.props.projectRoot,
      maxWarningLineLength: this.props.maxWarningLineLength,
    });
  }

  finish() {}

  // Override if you want to catch something specific with your regex
  prettyFormat(text: string) {
    return this.parser.parse(text);
  }

  // If you want to print inline, override #optionalNewline with ''
  optionalNewline() {
    return '\n';
  }

  getBuildSummary(): string {
    return `\n\u203A ${this.errors.length} error(s), and ${this.warnings.length} warning(s)\n`;
  }

  checkForBundlingErrors(lines: string | string[]) {
    lines = Array.isArray(lines) ? lines : lines.split('\n');
    // Find the last line beginning with `Error:` in the logs
    const lastErrorIndex = findLastIndex(lines);

    // Unless we find an error, we don't need to do anything
    if (lastErrorIndex === -1) {
      return;
    }

    // Take 12 lines from the `Error:` line forwards
    const block = lines.slice(lastErrorIndex, lastErrorIndex + 11).join('\n');
    const result = this.formatError(block);

    this.errors.push(result);
  }
}

function findLastIndex(arr: string[]) {
  for (let i = arr.length - 1; i >= 0; i--) {
    if (arr[i].startsWith('Error:')) {
      return i;
    }
  }
  return -1;
}

function formatPaths(config: { filePath: string | null; line?: number; col?: number }) {
  const filePath = chalk.reset.cyan(config.filePath);
  return (
    chalk.dim('(') +
    filePath +
    chalk.dim(`:${[config.line, config.col].filter(Boolean).join(':')})`)
  );
}

/**
 * Split a string like `/Users/foo/bar.m:420:68` into its components.
 *
 * @param filePath '/Users/foo/bar.m:420:68'
 */
function splitPathInfo(
  filePathAndLocation: string
): {
  filePath: string;
  lineNumber?: string;
  columnNumber?: string;
} {
  const [path, line, column] = filePathAndLocation.split(':');

  return {
    filePath: path || filePathAndLocation,
    lineNumber: line,
    columnNumber: column,
  };
}

function parseOptionalInt(text?: string): number | undefined {
  if (!text) return undefined;
  try {
    const result = parseInt(text, 10);
    return isNaN(result) ? undefined : result;
  } catch {
    return undefined;
  }
}

function formatWarningOrError({
  projectRoot,
  filePath,
  reason,
  cursor,
  lineText,
  lineNumber,
  columnNumber,
  isError,
  maxWarningLineLength = 200,
}: {
  projectRoot: string;
  filePath: string;
  reason: string;
  cursor?: string;
  lineText?: string;
  lineNumber?: string;
  columnNumber?: string;
  isError: boolean;
  testName?: string;
  maxWarningLineLength?: number;
}) {
  const line = parseOptionalInt(lineNumber) || 0;
  const column = parseOptionalInt(columnNumber);
  const color = isError ? chalk.red : chalk.yellow;
  const icon = color(isError ? ERROR : WARNING);

  const displayFilePath = !filePath
    ? // If no file path, use null
      null
    : // If the file path is inside of the build folder (Hermes), then use absolute path.
    getAppRoot(filePath)
    ? filePath
    : // Otherwise, use relative path
      slash(path.relative(projectRoot, filePath));

  const formattedPath = formatPaths({
    filePath: displayFilePath,
    col: column,
    line,
  });

  const pathWithPrefix = `${icon} ${formattedPath}`;

  const formattedReason = color(grayOutMatch(reason, /(\[-.*?\])/).replace(/(\(.*?\)\s?)/, ''));

  // Add special case for .jsbundle files that are parsed with Hermes.
  const isHermes = filePath.endsWith('.jsbundle');
  const isPreviewTooLong = isHermes || (lineText && lineText.length > maxWarningLineLength);
  // When the preview is too long, we skip reading the file and attempting to apply
  // code coloring, this is because it can get very slow.
  if (isPreviewTooLong) {
    let previewLine = '';
    let cursorLine = formattedReason;

    // Create a curtailed preview line like:
    // `...transition:'fade'},k._updatePropsStack=function(){clearImmediate(k._updateImmediate),k._updateImmediate...`
    // If there is no text preview or column number, we can't do anything.
    if (lineText && column != null) {
      const rangeWindow = Math.round(Math.max(displayFilePath?.length ?? 0, 80) / 2);
      let minBounds = Math.max(0, column - rangeWindow);
      const maxBounds = Math.min(minBounds + rangeWindow * 2, lineText.length);
      previewLine = lineText.slice(minBounds, maxBounds);

      // If we splice content off the start, then we should append `...`.
      // This is unlikely to happen since we limit the activation size.
      if (minBounds > 0) {
        // Adjust the min bounds so the cursor is aligned after we add the "..."
        minBounds -= 3;
        previewLine = chalk.dim('...') + previewLine;
      }
      if (maxBounds < lineText.length) {
        previewLine += chalk.dim('...');
      }

      // If the column property could be found, then use that to fix the cursor location which is often broken in regex.
      cursorLine =
        (column == null ? chalk.reset(cursor) : fill(column) + chalk.reset('^')).slice(minBounds) +
        ' ' +
        formattedReason;
    }

    return ['', pathWithPrefix, '', previewLine, cursorLine, chalk.dim('(warning truncated)')].join(
      '\n'
    );
  }

  try {
    const raw = fs.readFileSync(filePath, 'utf8');
    const location = { start: { line, column } };
    const framed = codeFrameColumns(raw, location, {
      // TODO: Support iOS languages: C++, Objc, swift, Ruby, Bash
      // Maybe something like prism but for terminals?
      highlightCode: false,
      // Remove `(_Nonnull, _Nullable, or _Null_unspecified)` options
      message: formattedReason,
    });

    return `\n${pathWithPrefix}\n\n${framed}\n`;
  } catch {
    // If the column property could be found, then use that to fix the cursor location which is often broken in regex.
    const customCursor = column == null ? chalk.reset(cursor) : fill(column) + chalk.reset('^');

    const framed = `${lineText}\n${customCursor} ${formattedReason}`;

    return `\n${pathWithPrefix}\n\n${framed}\n`;
  }
}

function fill(width: number): string {
  return Array(width).join(' ');
}

// Dim values like `[-Wnullability-completeness]`
function grayOutMatch(text: string, reg: RegExp): string {
  return replaceMatch(text, reg, chalk.gray.dim);
}

function replaceMatch(text: string, reg: RegExp, callback: (text: string) => string): string {
  const match = text.match(reg);
  if (match?.length) {
    return text.replace(reg, callback(match[0]));
  }
  return text;
}

function slash(path: string) {
  const isExtendedLengthPath = /^\\\\\?\\/.test(path);
  const hasNonAscii = /[^\u0000-\u0080]+/.test(path); // eslint-disable-line no-control-regex

  if (isExtendedLengthPath || hasNonAscii) {
    return path;
  }

  return path.replace(/\\/g, '/');
}
